import type { Consent, Privacy } from '@tunein/reporting-schemas';
import { keyMirror } from '@tunein/web-utils';

/** The regimes the reporting schema carries a consent decision for. */
type ReportedRegime = {
  [Key in keyof Privacy]: Privacy[Key] extends Consent ? Key : never;
}[keyof Privacy];

/**
 * The privacy regimes reported on the Unified Events common context. Exactly one applies to a
 * session, because OneTrust applies exactly one geolocation rule.
 *
 * Kept exhaustive against the reporting schema: a regime added to the proto fails to compile here
 * until it is resolvable from a OneTrust rule.
 */
export const jurisdictions = keyMirror(
  'gdpr',
  'globalOptIn',
  'globalOptOut',
  'usPrivacy',
) satisfies Record<ReportedRegime, ReportedRegime>;

export type ConsentJurisdiction = (typeof jurisdictions)[keyof typeof jurisdictions];

/**
 * The consent template behind a OneTrust geolocation rule, as published in the rule set JSON.
 * These are the only types our dashboard uses; anything else resolves to no jurisdiction rather
 * than being guessed at.
 */
export const ruleTypes = {
  tcfV2: 'IAB2V2',
  gdpr: 'GDPR',
  cpra: 'CPRA',
} as const;
